import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `search:` scope (F8/F30/F31), following the
 * exact `documents:`/`notifications:` precedent (`apps/web/src/routes/
 * documents/documents.i18n.ts`) — NOT a second i18n runtime (E18/§13.3 ban),
 * just physically scoped here because `packages/i18n/**` is outside this
 * task's ownership boundary.
 *
 * OPEN ISSUE: fold into `packages/i18n/src/catalogs/{en,ro}/search.json`.
 */
const en = {
  'page.title': 'Search',
  'input.label': 'Search documents and discoveries',
  'input.placeholder': 'Search by title or content…',
  'input.clear': 'Clear search',
  'hint.belowMinChars': 'Keep typing — at least 2 characters',
  'input.resultsCount': '{count, plural, one {# result} other {# results}}',
  'filters.kind.label': 'Type',
  'filters.kind.any': 'Any type',
  'filters.kind.book': 'Book',
  'filters.kind.article': 'Article',
  'filters.kind.report': 'Report',
  'filters.difficulty.label': 'Difficulty',
  'filters.difficulty.any': 'Any difficulty',
  'filters.difficulty.basic': 'Basic',
  'filters.difficulty.intermediate': 'Intermediate',
  'filters.difficulty.advanced': 'Advanced',
  'filters.difficulty.mastery': 'Mastery',
  'filters.language.label': 'Language',
  'filters.language.placeholder': 'e.g. en, ro',
  'filters.sort.label': 'Sort by',
  'filters.sort.relevance': 'Relevance',
  'filters.sort.updatedAt': 'Recently updated',
  'filters.categories.all': 'All categories',
  'filters.categories.label': 'Filter by category',
  'result.publishedOn': 'Published {date}',
  'result.hideAction': 'Hide from search results',
  'result.hideTooltip': 'Never show this again in your search results',
  'result.open': 'Open',
  'empty.firstUse.title': 'Search your documents and public discoveries',
  'empty.firstUse.description': 'Type a title, topic, or keyword to find documents, discoveries, and public content.',
  'empty.noResults.title': 'No results for “{query}”',
  'empty.noResults.description': 'Try a different search term or clear your filters.',
  'empty.error.title': "Couldn't load search results.",
  'toast.hidden.action': 'Hidden',
  'toast.hideFailed': "Couldn't hide this result — try again.",
  'toast.undoFailed': "Couldn't restore this result — try searching again.",
} as const;

const ro: Record<keyof typeof en, string> = {
  'page.title': 'Căutare',
  'input.label': 'Caută documente și descoperiri',
  'input.placeholder': 'Caută după titlu sau conținut…',
  'input.clear': 'Șterge căutarea',
  'hint.belowMinChars': 'Continuă să scrii — cel puțin 2 caractere',
  'input.resultsCount': '{count, plural, one {# rezultat} other {# rezultate}}',
  'filters.kind.label': 'Tip',
  'filters.kind.any': 'Orice tip',
  'filters.kind.book': 'Carte',
  'filters.kind.article': 'Articol',
  'filters.kind.report': 'Raport',
  'filters.difficulty.label': 'Dificultate',
  'filters.difficulty.any': 'Orice dificultate',
  'filters.difficulty.basic': 'De bază',
  'filters.difficulty.intermediate': 'Intermediar',
  'filters.difficulty.advanced': 'Avansat',
  'filters.difficulty.mastery': 'Măiestrie',
  'filters.language.label': 'Limbă',
  'filters.language.placeholder': 'ex. en, ro',
  'filters.sort.label': 'Sortează după',
  'filters.sort.relevance': 'Relevanță',
  'filters.sort.updatedAt': 'Actualizat recent',
  'filters.categories.all': 'Toate categoriile',
  'filters.categories.label': 'Filtrează după categorie',
  'result.publishedOn': 'Publicat {date}',
  'result.hideAction': 'Ascunde din rezultatele căutării',
  'result.hideTooltip': 'Nu mai afișa asta niciodată în rezultatele tale de căutare',
  'result.open': 'Deschide',
  'empty.firstUse.title': 'Caută în documentele tale și în descoperirile publice',
  'empty.firstUse.description': 'Scrie un titlu, o temă sau un cuvânt cheie pentru a găsi documente, descoperiri și conținut public.',
  'empty.noResults.title': 'Niciun rezultat pentru „{query}”',
  'empty.noResults.description': 'Încearcă alt termen de căutare sau șterge filtrele.',
  'empty.error.title': 'Nu am putut încărca rezultatele căutării.',
  'toast.hidden.action': 'Ascuns',
  'toast.hideFailed': 'Nu am putut ascunde acest rezultat — încearcă din nou.',
  'toast.undoFailed': 'Nu am putut restaura acest rezultat — încearcă să cauți din nou.',
};

export const SEARCH_CATALOG: Record<Locale, Record<keyof typeof en, string>> = { en, ro };

export type SearchCatalogKey = keyof typeof en;
